import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import {
  coatingThicknessFormField,
  tminFormField,
  wallThicknessFormField,
} from 'types/assetModel/commonFields'
import {
  FeatureTableOptions,
  GeckoEditableFeature,
} from 'types/assetModel/features/baseClasses/geckoEditableFeature'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { parseNumber, parseString } from 'utils/parsingHelpers'
import { requiredNumberSchema, requiredStringSchema } from 'utils/zodHelpers'

export interface GeckoConeCourseJSON {
  type: GeckoFeatureModelType.ConeCourse
  name: string
  feature_geometry: {
    y_end: number
  }
  tmin: number | undefined
  nominal: {
    wall_thickness: number | undefined
    coating_thickness: number | undefined
  }
}

export const NewGeckoConeCourseJSON: GeckoConeCourseJSON = {
  type: GeckoFeatureModelType.ConeCourse,
  name: 'Course',
  feature_geometry: {
    y_end: 0,
  },
  tmin: undefined,
  nominal: {
    wall_thickness: undefined,
    coating_thickness: undefined,
  },
}

export class GeckoConeCourse extends GeckoEditableFeature {
  public readonly name: string
  public readonly type = GeckoFeatureModelType.Course
  public readonly geometry: GeckoComponentRect
  public readonly wallNominalThickness: GeckoMeasurement | undefined
  public readonly coatingNominalThickness: GeckoMeasurement | undefined
  public tmin: GeckoMeasurement | undefined

  private constructor(
    id: string,
    name: string,
    geometry: GeckoComponentRect,
    wallNominal: GeckoMeasurement | undefined,
    coatingNominal: GeckoMeasurement | undefined,
    tmin: GeckoMeasurement | undefined
  ) {
    super(id)
    this.name = name
    this.geometry = geometry
    this.wallNominalThickness = wallNominal
    this.coatingNominalThickness = coatingNominal
    this.tmin = tmin
  }

  public get componentID() {
    return this.geometry.componentID
  }

  public static CreateArrayFromJSON(
    json: unknown,
    componentGeometry: GeckoComponentRect,
    thicknessMeasurementUnit: GeckoMeasurementUnit,
    jsonIDs?: Array<string>
  ): ReadonlyArray<GeckoConeCourse> | undefined {
    const arr = json as
      | Array<Record<string, string | number | Record<string, unknown>>>
      | undefined
    if (!arr) return undefined

    try {
      const basicCourseData: {
        id: string
        name: string
        yEnd: number
        nominal: GeckoConeCourseJSON['nominal']
        tmin: number | undefined
      }[] = arr
        .map((course, index) => {
          const featureGeometry = course.feature_geometry as Record<
            string,
            number
          >
          const yEnd =
            parseNumber(featureGeometry?.y_end ?? course.y_end) ??
            componentGeometry.end.y.num
          const nominal = course.nominal as GeckoConeCourseJSON['nominal']
          const tmin = course.tmin as number | undefined

          return {
            id: jsonIDs ? jsonIDs[index] : String(index),
            name: parseString(course.name) ?? '',
            yEnd,
            nominal,
            tmin,
          }
        })
        .toSorted((courseA, courseB) => {
          return courseA.yEnd - courseB.yEnd
        })

      let priorYEnd = componentGeometry.start.y.num

      return basicCourseData.map((bcdIterator) => {
        if (priorYEnd > bcdIterator.yEnd) {
          throw Error(
            `Cone courses not sorted for ${componentGeometry.componentID}`
          )
        }

        const coneCourseBounds = GeckoComponentRect.CreateFromXYs(
          componentGeometry.componentID,
          componentGeometry.start.x.num,
          priorYEnd,
          componentGeometry.end.x.num,
          bcdIterator.yEnd,
          componentGeometry.x.unit,
          componentGeometry.y.unit
        )
        const wallNominal = parseNumber(bcdIterator.nominal?.wall_thickness)
        const coatingNominal = parseNumber(
          bcdIterator.nominal?.coating_thickness
        )
        const tmin = parseNumber(bcdIterator.tmin)

        const result = new GeckoConeCourse(
          bcdIterator.id,
          bcdIterator.name,
          coneCourseBounds,
          wallNominal === undefined
            ? undefined
            : GeckoMeasurement.CreateFromValue(
                wallNominal,
                thicknessMeasurementUnit
              ).convertTo(GeckoMeasurementUnit.Inches),
          coatingNominal === undefined
            ? undefined
            : GeckoMeasurement.CreateFromValue(
                coatingNominal,
                thicknessMeasurementUnit
              ).convertTo(GeckoMeasurementUnit.Inches),
          tmin === undefined
            ? undefined
            : GeckoMeasurement.CreateFromValue(
                tmin,
                thicknessMeasurementUnit
              ).convertTo(GeckoMeasurementUnit.Inches)
        )
        priorYEnd = bcdIterator.yEnd
        return result
      })
    } catch (e) {
      console.error(e)
      return undefined
    }
  }

  public static CreateMapFromJSON(
    coursesJSON: Record<string, GeckoConeCourseJSON>,
    componentGeometry: GeckoComponentRect,
    thicknessMeasurementUnit: GeckoMeasurementUnit
  ): Map<string, GeckoConeCourse> {
    const array = this.CreateArrayFromJSON(
      Object.values(coursesJSON),
      componentGeometry,
      thicknessMeasurementUnit,
      Object.keys(coursesJSON)
    )
    const result = new Map<string, GeckoConeCourse>()
    array?.forEach((course) => {
      result.set(course.id, course)
    })
    return result
  }

  public static CreateFromJSON(
    componentID: string,
    courseID: string,
    courseJSON: GeckoConeCourseJSON
  ): GeckoConeCourse {
    // This is a fake component bounds, because all we really care about is y_end
    const pseudoComponentBounds = GeckoComponentRect.CreateFromXYs(
      componentID,
      0,
      0,
      10,
      courseJSON.feature_geometry.y_end as number,
      GeckoMeasurementUnit.Meters,
      GeckoMeasurementUnit.Meters
    )

    // This is just so we can reuse the creation workflow and don't have to re-write it
    const instantiatedMap = GeckoConeCourse.CreateMapFromJSON(
      { [courseID]: courseJSON } as Record<string, GeckoConeCourseJSON>,
      pseudoComponentBounds,
      GeckoMeasurementUnit.Meters
    )

    const instantiatedCourse = instantiatedMap.get(courseID)
    if (!instantiatedCourse) {
      throw new Error('Unable to retrieve the instantiated Cone Course!')
    }

    return instantiatedCourse
  }

  public asJSON() {
    return generateConeCourse(
      this.name,
      this.geometry.y.end,
      this.wallNominalThickness,
      this.coatingNominalThickness,
      this.tmin
    )
  }
}

export function generateConeCourse(
  name: string,
  yEnd: GeckoMeasurement,
  wallNominalThickness?: GeckoMeasurement,
  coatingNominalThickness?: GeckoMeasurement,
  tmin?: GeckoMeasurement
): GeckoConeCourseJSON {
  const courseJSON: GeckoConeCourseJSON = {
    type: GeckoFeatureModelType.ConeCourse,
    name: name,
    feature_geometry: {
      y_end: yEnd.toNumber(GeckoMeasurementUnit.Meters),
    },
    tmin: undefined,
    nominal: {
      wall_thickness: undefined,
      coating_thickness: undefined,
    },
  }

  if (wallNominalThickness !== undefined) {
    courseJSON.nominal.wall_thickness = wallNominalThickness.toNumber(
      GeckoMeasurementUnit.Meters
    )
  }

  if (coatingNominalThickness !== undefined) {
    courseJSON.nominal.coating_thickness = coatingNominalThickness.toNumber(
      GeckoMeasurementUnit.Meters
    )
  }
  if (tmin !== undefined) {
    courseJSON.tmin = tmin.toNumber(GeckoMeasurementUnit.Meters)
  }

  return courseJSON
}

export const ConeCourseFormFields = (courseJSON: GeckoConeCourseJSON) => {
  return [
    {
      dataKey: 'name',
      label: 'Course Name',
      value: courseJSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'y_end',
      label: 'Y End',
      value: courseJSON.feature_geometry.y_end,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    wallThicknessFormField(courseJSON.nominal.wall_thickness),
    coatingThicknessFormField(courseJSON.nominal.coating_thickness),
    tminFormField(courseJSON.tmin),
  ]
}

export const ConeCourseJSONFromFormFields = (formFields: FormField[]) => {
  const wallThickness = getFormFieldByDataKey(formFields, 'wall_thickness')
  const coatingThickness = getFormFieldByDataKey(
    formFields,
    'coating_thickness'
  )
  const tmin = getFormFieldByDataKey(formFields, 'tmin')
  return generateConeCourse(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'y_end')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    wallThickness?.value !== undefined
      ? GeckoMeasurement.CreateFromValue(
          wallThickness?.value as number,
          GeckoMeasurementUnit.Meters
        )
      : undefined,
    coatingThickness?.value !== undefined
      ? GeckoMeasurement.CreateFromValue(
          coatingThickness?.value as number,
          GeckoMeasurementUnit.Meters
        )
      : undefined,
    tmin?.value !== undefined
      ? GeckoMeasurement.CreateFromValue(
          tmin?.value as number,
          GeckoMeasurementUnit.Meters
        )
      : undefined
  )
}

export const ConeCourseFeatureTableOptions: FeatureTableOptions = {
  type: GeckoFeatureModelType.ConeCourse,
  title: 'Courses',
  singularName: 'Course',
  fields: ['name', 'y_end', 'wall_thickness'],
  sortBy: [{ fieldName: 'y_end', order: 'asc' }],
}
